import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, FormControl, InputLabel, Select, MenuItem,
  FormHelperText, Box, Typography, CircularProgress, Avatar
} from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import { fetchApprovers } from '../services/api';

export default function RejectionPopup({ open, record, onClose, onSubmit }) {
  const [rejectionReason, setRejectionReason] = useState('');
  const [rejectedBy, setRejectedBy] = useState('');
  const [rejectedDate, setRejectedDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [approvers, setApprovers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open && record) {
      setRejectionReason('');
      setRejectedBy('');
      setRejectedDate(new Date().toISOString().split('T')[0]);
      setErrors({});
      
      const reqId = record.loan_request_id || record.request_id;
      if (reqId) {
        setLoading(true);
        fetchApprovers(reqId)
          .then(res => {
            setApprovers(res.data?.data || []);
          })
          .catch(err => {
            console.error('Error fetching approvers in RejectionPopup:', err);
          })
          .finally(() => {
            setLoading(false);
          });
      } else {
        setApprovers([]);
      }
    }
  }, [open, record]);

  const validate = () => {
    const errs = {};
    if (!rejectionReason.trim()) {
      errs.rejectionReason = 'Rejection Reason is required';
    }
    if (!rejectedBy) {
      errs.rejectedBy = 'Rejected By is required';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    
    setSubmitting(true);
    try {
      await onSubmit({
        rejection_reason: rejectionReason.trim(),
        rejected_by: rejectedBy,
        rejected_date: rejectedDate
      });
    } catch (err) {
      console.error('Error in RejectionPopup submit:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          overflow: 'hidden',
          boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
        }
      }}
    >
      {/* Top accent line */}
      <Box sx={{
        height: 4,
        background: 'linear-gradient(90deg, #DC2626, #EF4444)'
      }} />

      <DialogTitle sx={{ px: 3, pt: 3, pb: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Avatar sx={{ bgcolor: '#FEF2F2', width: 40, height: 40 }}>
          <BlockIcon sx={{ color: '#DC2626', fontSize: 20 }} />
        </Avatar>
        <Box>
          <Typography variant="h6" fontWeight={800} color="#111827">
            Reject Request
          </Typography>
          <Typography variant="body2" color="#6B7280">
            Provide a reason and select the approver to reject this request.
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
          {/* Rejection Reason */}
          <TextField
            label="Rejection Reason *"
            variant="outlined"
            multiline
            rows={3}
            fullWidth
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            error={!!errors.rejectionReason}
            helperText={errors.rejectionReason || "Please explain the reason for rejection"}
            disabled={submitting}
            InputProps={{
              sx: { borderRadius: '10px' }
            }}
          />

          {/* Rejected Date (Read-Only) */}
          <TextField
            label="Rejected Date"
            variant="outlined"
            fullWidth
            value={rejectedDate}
            disabled
            InputProps={{
              readOnly: true,
              sx: { borderRadius: '10px', bgcolor: '#F9FAFB' }
            }}
          />

          {/* Rejected By Select Dropdown */}
          <FormControl fullWidth error={!!errors.rejectedBy} disabled={submitting}>
            <InputLabel id="rejected-by-label">Rejected By *</InputLabel>
            <Select
              labelId="rejected-by-label"
              id="rejected-by-select"
              value={rejectedBy}
              label="Rejected By *"
              onChange={(e) => setRejectedBy(e.target.value)}
              sx={{ borderRadius: '10px' }}
              endAdornment={loading ? <CircularProgress size={20} sx={{ mr: 2 }} /> : null}
            >
              {loading ? (
                <MenuItem value="" disabled>
                  Loading approvers...
                </MenuItem>
              ) : approvers.length === 0 ? (
                <MenuItem value="" disabled>
                  No policy approvers found
                </MenuItem>
              ) : (
                approvers.map((appr) => (
                  <MenuItem key={appr.id} value={appr.id}>
                    {appr.name} ({appr.id})
                  </MenuItem>
                ))
              )}
            </Select>
            <FormHelperText>
              {errors.rejectedBy || "Select the designated approver rejecting this request"}
            </FormHelperText>
          </FormControl>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 1, display: 'flex', gap: 1.5 }}>
        <Button
          onClick={onClose}
          disabled={submitting}
          fullWidth
          variant="outlined"
          sx={{
            py: 1.2, fontWeight: 600, fontSize: 14,
            borderColor: '#E2E8F0', color: '#64748B', borderRadius: '10px',
            '&:hover': { bgcolor: '#F8FAFC', borderColor: '#94A3B8' },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={submitting || loading}
          fullWidth
          variant="contained"
          sx={{
            py: 1.2, fontWeight: 700, fontSize: 14,
            bgcolor: '#DC2626', borderRadius: '10px',
            boxShadow: '0 4px 14px rgba(220, 38, 38, 0.3)',
            '&:hover': { bgcolor: '#B91C1C', boxShadow: '0 6px 18px rgba(220, 38, 38, 0.4)' },
          }}
        >
          {submitting ? <CircularProgress size={20} color="inherit" /> : 'Submit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
